// D4 — Web Documentation: canonical backend search API client (TechSpec §19-22).
//
// D4 replaces the D2 client-side search boundary with the canonical
// deterministic, ACL-first backend search (backend/app/routers/docs.py →
// /api/docs/search). The backend is the SINGLE security boundary:
//  * results/snippets/counts are computed ONLY over the caller-authorized
//    set (TechSpec §21) — the frontend never filters security-correctly;
//  * the ``context`` payload (UI Context Contract) is a RANKING signal only —
//    the client ``user_role`` is NEVER an authorization authority (the
//    authoritative role comes from the authenticated JWT).
//
// The article BODY is still served from the build-time canonical corpus
// (content.ts) for user-domain articles: user articles are public product
// docs (not restricted content), so bundling them is safe (D2-A10 parity).
// Search itself — and everything security-sensitive — is backend-authoritative.

import { request } from "@/lib/api";

/** One search result row (backend /api/docs/search contract). */
export interface DocsSearchResult {
  title: string;
  slug: string;
  section: string;
  audience: "user" | "rcc";
  platforms: string[];
  status: "current" | "deprecated" | "future";
  version: number;
  description: string;
  snippet: string;
  score: number;
  related: string[];
}

export interface DocsSearchResponse {
  query: string;
  results: DocsSearchResult[];
  total: number;
}

export interface DocsSearchContext {
  /** Surface the user is on: "web" (Web docs scope) / "rcc". RANKING ONLY. */
  surface?: "web" | "rcc";
  /** Route/screen key (e.g. "web.chat"). RANKING ONLY. */
  route?: string;
  /** Corpus section id (e.g. "chat"). RANKING ONLY. */
  section?: string;
  /** Article help_context_key — strongest ranking boost. RANKING ONLY. */
  help_context_key?: string;
  /** Platform scope: "web" | "android" | "rcc" | "web+android". FILTER. */
  platform?: string;
  /** Client-claimed role — NEVER an authorization authority. RANKING ONLY. */
  user_role?: string;
}

export interface DocsSearchOptions {
  platform?: "web" | "android" | "rcc" | "web+android";
  status?: "current" | "deprecated" | "future";
  context?: DocsSearchContext;
  signal?: AbortSignal;
}

/**
 * One article by canonical slug (backend /api/docs/articles/{slug}).
 * 404 for unknown OR not-authorized (anti-enumeration, no existence leak —
 * TechSpec §21). Used to render article bodies that are not in the bundled
 * user corpus (the Web corpus is the build-time user/* domain).
 */
export async function fetchDocArticle(
  slug: string,
  signal?: AbortSignal,
): Promise<DocsArticleFull> {
  return request<DocsArticleFull>(
    `/api/docs/articles/${encodeURIComponent(slug)}`,
    { signal },
  );
}

export interface DocsArticleFull {
  slug: string;
  title: string;
  section: string;
  audience: "user" | "rcc";
  platforms: string[];
  status: "current" | "deprecated" | "future";
  version: number;
  description: string;
  body: string;
  related: string[];
  last_reviewed: string | null;
  requires_auth: boolean;
}

/**
 * Canonical documentation search (D4). ACL-first, deterministic, ACL applied
 * server-side BEFORE any result is materialized.
 *
 * Throws ApiError on failure; the UI treats 401/403 as "session/role" and
 * 400 (malformed context) as a validation response — never renders content
 * it did not receive.
 */
export async function searchDocs(
  query: string,
  options: DocsSearchOptions = {},
): Promise<DocsSearchResponse> {
  const params = new URLSearchParams();
  params.set("q", query);
  if (options.platform) params.set("platform", options.platform);
  if (options.status) params.set("status", options.status);
  if (options.context && Object.keys(options.context).length > 0) {
    params.set("context", JSON.stringify(options.context));
  }
  return request<DocsSearchResponse>(
    `/api/docs/search?${params.toString()}`,
    { signal: options.signal },
  );
}


